import { DealsOrderBy, DealViews } from "constants/deal";
import { IStore } from "store";

import {
  CharacteristicDataType,
  DealFolderForm,
  DealForm,
  FolderKind,
  ICharContactFieldVisibility,
  IDashboardShare,
  IEmail,
  IFile,
  IFileMeta,
  IFolder,
  IInvestmentDetailChartConfig,
  INewsMeta,
  INote,
  INoteLock,
  INoteMeta,
  InvestorWithBank,
  IReachOutManage,
  IRound,
  IWorkspaceMetadata,
  Measure,
  MeasureDataTableFilters,
  MeasureParameter
} from ".";
import {
  CurrencyType,
  ICubic,
  IDealRoundsWithInvestors,
  IOpportunities,
  // IQuestionnaire,
  IRoleName,
  Nullable,
  ReportFilters,
  ReportsQueries,
  TableData,
  // SurveyKindType,
  UserLight
} from "..";
// import { ApiServiceParams } from "store/services";
import { RoleName } from "../../../constants";
import { IUploadingProgress } from "./file";

export interface IDealPreview {
  id: number;
  account_id: number;
  user_id: number;
  asset_class_id: number;
  sub_asset_class_id: number | null;
  title: string;
  asset_class: IAssetClass;
  sub_asset_class: ISubAssetClass;
  deal_capital_seeker: ICapitalSeeker[];
  currency: DealBarCharacteristic | null;
  geography: DealBarCharacteristic | null;

  deal_owner_value: string;
  status: IStatus;
  email: string;
  logo: IFile | null;
  deal_role: RoleName | null;
  inviter: UserLight | null;

  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}
export interface IDealsTableRow {
  id: number;
  title: string;
  created_at: string;
  firm: string;
  asset_class: string;
  deal_status: string;
  minimum_ticket: number | string;
  currency: CurrencyType;
  user_id: number;
  deal_capital_seeker_email: string;
}

export interface DealAssetSimple {
  id: number;
  name: string;
}

export type DealsTableCell = string | number | null | DealBarCharacteristic | DealAssetSimple;

export type DealsTable = TableData<{ id: number } & Record<string, DealsTableCell>>;

export interface IGetDealsData {
  filters?: ReportFilters;
  page: number;
  deal_title?: string;
  order_by?: DealsOrderBy;
  columns?: string[];
  deals_group_by?: string[];
  view?: "table";
}

export interface IDealModule {
  deal: IDeal;
  statuses: IStatus[];
  uploadingProgress: IUploadingProgress[];
  fileMeta: Partial<IFileMeta>;
  folderMeta: Partial<IWorkspaceMetadata>;
  newsMeta: Partial<INewsMeta>;
  emailMeta: IEmail | null;
  noteMeta: Partial<INoteMeta>;
  dealShareLoading: boolean;
  investors: InvestorWithBank[];
  noteLock: INoteLock;
  measureParameters: MeasureParameter[];
  queries: DealsQueries;
  logoToSave: IFile | null;
  hierarchyPanelOpen: boolean;
  //  ----------------- New structure -------------------
  folders: Record<number, IFolder>;
  forms: Record<number, DealFolderForm>;
  files: Record<number, IFile>;
  notes: Record<number, INote>;
  emails: Record<number, IEmail>;
  // rounds: Record<number, IRound>;
  // commitments: Record<number, ICommitment>;

  characteristicsManage: ICharacteristicsManageData | null;
  measureDataTableFilters: MeasureDataTableFilters;

  measures: Measure[];
  reachOutManage: IReachOutManage | null;

  opportunities: IOpportunities[];
  shares: IDashboardShare[];

  dealFilters: ReportFilters;
  form: DealForm;
  roundsWithInvestors: IDealRoundsWithInvestors[];
  documentTypes: string[];
}

export interface ICharacteristicsManageData {
  dealId: number;
  dealName: string;
  viewOnly?: boolean;
  characteristicId?: number;
  isContact?: boolean;
  onSubmitSuccess?: () => void | Promise<void>;
  /** When opening from "Review missing fields": target stage name for "Apply and move to XYZ" */
  targetStageName?: string;
  /** When opening from "Review missing fields": stage id to move deal to after apply */
  requiredForStageId?: string;
  /** When opening from "Review missing fields": characteristic names to highlight (yellow) */
  missingFieldNames?: string[];
}

export interface IInvestorViewTable {
  measures: string[];
  characteristics: string[];
  calculatedFields: string[];
}

export interface IDealImportValidateError {
  parameter: string;
  reason: string;
  expectedFormat: string;
  /** Value from the uploaded file that failed validation */
  value?: string;
}

export interface IDealImportValidateResponse {
  valid: boolean;
  dealName?: string;
  asset_class_name?: string | null;
  characteristics?: Array<{ name: string; value: string }>;
  errors?: IDealImportValidateError[];
  newCharacteristics?: string[];
}

export interface IDealAIExtractResponse {
  deal_name: string | null;
  asset_class_id: number | null;
  sub_asset_class_id: number | null;
  deal_stage_id: number | null;
  currency: string | null;
  geography: string | null;
  characteristics: Array<{ characteristic_id: number; value: string }>;
  template_id: number | null;
  contact: Pick<
    ICapitalSeeker,
    "email" | "firm" | "first_name" | "last_name" | "title" | "phone_number" | "address"
  > | null;
}

export interface DealsQueries {
  filters: ReportFilters;
  deal_title: string;
  view: DealViews;
  order_by: DealsOrderBy;
  tableColumns: string[];
  dealListGroupBy: string[];
  cubicInvestorView: ICubic;
  chartInvestorView: ReportsQueries["graphDataFormation"];
  summaryTableInvestorView: IInvestorViewTable;
  descriptionTableInvestorView: IInvestorViewTable;
  /** Pipeline view: ordered grouping dimension keys (max 2). Empty = no grouping. */
  pipelineGroupBy: string[];
  /** Pipeline view: CHECKED characteristic names shown as chips on cards (max 5). */
  pipelineCardCharacteristics: string[];
  /** Pipeline view: the Card details LIST — checkbox rows the user can grow via "Add Characteristic". */
  pipelineCardCharacteristicOptions: string[];
  /** Pipeline view: stage ids hidden via the Deal Stages settings tab. */
  pipelineHiddenStageIds: number[];
}

interface PlainDeal {
  id: number;
  account_id: number;
  account_logo: string;
  user_id: number;
  asset_class_id: number;
  sub_asset_class_id: number | null;
  type: string | null;
  title: string;
  confirm_change: boolean;
  status: IStatus;
  email: string;

  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface IDeal extends PlainDeal {
  logo: IFile | null;
  asset_class: IAssetClass;
  sub_asset_class: ISubAssetClass;
  deal_capital_seeker: ICapitalSeeker[];
  chart_settings: Record<string, IInvestmentDetailChartConfig>;

  currency: DealBarCharacteristic | null;
  geography: DealBarCharacteristic | null;
  vintage: DealBarCharacteristic | null;

  rounds: IRound[];
  parent: Nullable<PlainDeal & { rounds_count: number }>;
  deal_stage_id: number;
  sub_assets: PlainDeal[];
  opportunity_id?: number;
  crunchbase_data_id: number | null;
  crypto_coin_icon: string | null;
  deal_role: IRoleName | null;
  nullify_unfunded_commitment: boolean;
  management_fee_setting?: {
    id: number;
    account_id: number;
    deal_id: number;
    method: string;
    start_date: string;
    end_date: string;
    commitment_fees_inv: number | null;
    cost_fees_inv: number | null;
    commitment_fees_post: number | null;
    cost_fees_post: number | null;
    nav_rate: number | null;
    differential_data: {
      rate: number;
      type: string;
      year: number;
    }[];
    created_at: string;
    updated_at: string;
  };
  success_fee_setting?: {
    deal_id: number;
    account_id: number;
    hurdle_1: number;
    catch_up_1: number;
    carry_1: number;
    created_at: string;
    updated_at: string;
  };
  rating: string | null;
}

export interface IAssetClass {
  id: number;
  name: string;
  presentation_name: string;
  parent_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  sub_asset_class: IAssetClass[];

  account_id: number | null;
  account_alias: string | null;
  is_hidden: boolean;
  private_index: { id: number; name: string } | null;
  public_index: string | null;
  expected_return: number | null;
}

export interface ISubAssetClass {
  id: number;
  name: string;
  presentation_name: string;
  parent_id: number | null;
  created_at: string | null;
  updated_at: string | null;

  is_hidden: boolean;
  account_id: number | null;
  account_alias: string | null;
}

export interface ICapitalSeeker {
  id: number | string;
  account_id: number;
  deal_id: number;
  email: string;
  firm: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  title: string;
  address: string | null;
  country: string | null;
  referral: string;
  created_at: string | null;
  updated_at: string | null;
  profile_photo_path: string | null;
  is_show: ICharContactFieldVisibility;
}

export interface IUpdateCapitalSeekerData extends Partial<ICapitalSeeker> {
  dealId: number;
}

export interface IStatus {
  name: string;
  id: number;
  account_id: number | null;
  color: string;
}

export interface DealBarCharacteristic {
  id: number;
  name: string;
  is_multiple: boolean;
  data_type: CharacteristicDataType;

  data: Array<{
    id: number;
    deal_id: number;
    characteristic_id: number;
    value: string;
    percentage: number;
    is_default: boolean;
    is_currency: boolean;
    currency: string | null;
  }>;
}

export interface ICreateDealData {
  title: string;
  min_ticket?: number;
  asset_type_id: number;
  asset_class_id: number;
  status_id?: number;
  sub_asset_class_id?: number;
  template_id: number;
  deal_capital_seeker: Partial<{
    firm: string;
    email: string;
    first_name: string;
    last_name: string;
    title: string;
    phone_number: string;
    address: string;
    referral: string;
  }>[];
  deal_stage_id?: number;
  type?: string;
  currency?: string;
  geography?: string;
  characteristic_data?: Array<{
    id: string;
    deal_id: number;
    characteristic_id: number;
    value: string;
    currency: string | null;
    is_default: boolean;
    percentage: number;
    is_deleted: boolean;
    investor_id: null;
    round_id: null;
  }>;
  file?: File;
}

export interface IDealPatchData {
  title?: string;
  asset_class_id?: number;
  sub_asset_class_id?: number;
  confirm?: boolean;
  status_id?: number;
  min_ticket?: string;
  currency?: string;
  geography?: string;
  deal_stage_id?: number;
  dealId: number;
}

export type DealChunks = Pick<IDealModule, "folders" | "forms" | "files" | "notes" | "emails"> &
  Pick<IStore, "documents">;

export interface CrunchbaseData {
  id: number;
  name: string;
  logo: string;
  description: string;
  employees: number;
  locations: string;
  website: string;
  link: string;
  industries: string;
  founders: Array<{ name: string; logo: string }>;
  similar_companies: Array<{ name: string; logo: string }>;
  investors: Array<{ name: string; logo: string }>;
}

export interface ICryptocurrencyOption {
  key: string;
  value: string;
}

export interface IDealCryptoPerformanceData {
  date: string;
  price: string;
  market_cap: string;
  circulating_supply: string;
}

export type IDealCryptoGraph = Pick<IDealCryptoPerformanceData, "date" | "price">;

export interface IDealCryptocurrency {
  id: string;
  name: string;
  image: string;
  link: string;
  price: string;
  previous_close: string;
  market_cap: string;
  circulating_supply: string;
  graph_data: IDealCryptoGraph[];
  historical_data: IDealCryptoPerformanceData[];
}

export enum SearchItemType {
  Deal = "deal",
  File = "file",
  Document = "document",
  Characteristic = "characteristic",
  Form = "form",
  Folder = "folder"
}

export interface ISearchItem {
  result: string;
  deal_id: number;
  id: number;
  deal_title: string;
  type: SearchItemType;
  section_id: number | null;
  folder_kind: FolderKind | null;
  email_id: number | null;
}

export type WorkspaceFilterDateItem = { from: string; to: string };
export type WorkspaceFilterItem = string | number | boolean | { id: number; name: string } | WorkspaceFilterDateItem;

export type WorkspaceFilters = {
  [filterKey: string]: WorkspaceFilterItem[];
};

export type WorkspaceFiltersFromDB = {
  [folderType: string]: WorkspaceFilters;
};
